// TODO: use centralized component between here and activities-desking
import type { ComponentProps, ReactNode } from 'react';
import { useCallback } from 'react';

// constants
import { SUMMARY_ACCORDION_ID, ZERO_DOLLARS } from '../../constants';

// libraries
import type { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import { kebabCase } from '@makemydeal/dr-common-utils';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { ExpandableAreaProvider, useCheckExpandableAreaProvider } from '../../common/ExpandableArea';
import { skewWords } from '../../utils/formatUtils';
import { StyledListItem } from './DealSummaryItem.style';
import { DealSummaryProvider, useDealSummary } from '../../common/DealSummaryProvider';

import DealSummaryDetailedView from './DealSummaryDetailedView';
import DealSummaryExpandableView from './DealSummaryExpandableView';
import LineItemRow from './LineItemRow';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';

export interface IDealSummaryItemProps {
    label: string;
    value: string;
    children?: ReactNode;
    expandibleButtonStyles?: ComponentProps<typeof Grid>['sx'];
    expandibleAreaStyles?: ComponentProps<typeof Grid>['sx'];
    labelStyles?: ComponentProps<typeof Typography>['sx'];
    valueStyles?: ComponentProps<typeof Typography>['sx'];
    /**
     * @deprecated Unused prop
     */
    showLargeValue?: boolean;
    isEmpty?: boolean;
    /**
     * @deprecated Unused prop
     */
    isExpanded?: boolean;
    hasDelta?: boolean;
    scopeId?: string;
    defaultExpanded?: boolean;
    /**
     * Optional control rendered at the right end of the header row, after the value. Handlers on it
     * must call `stopPropagation` or the click will also toggle the section.
     */
    headerAction?: ReactNode;
    /**
     * Optional control rendered inline immediately after the label text (e.g. a tooltip info icon).
     * Only applied on items with no `children` (the non-expandable, non-detailed row).
     */
    labelAction?: ReactNode;
}

export const DealSummaryItem = ({
    label,
    value,
    children,
    isEmpty = false,
    hasDelta = false,
    expandibleButtonStyles,
    expandibleAreaStyles,
    labelStyles,
    valueStyles,
    scopeId,
    defaultExpanded,
    headerAction,
    labelAction
}: IDealSummaryItemProps) => {
    const offerType = useTrackedSelector(offerReduxSelectors.getCurrentOfferType, {
        component: 'DealSummaryItem',
        field: 'currentOfferType',
        emitValue: true
    });
    const isEnhancedDealDetailsEnabled = useTrackedSelector(featureToggleSelectors.getEnhancedDealDetails, {
        component: 'DealSummaryItem',
        field: 'enhancedDealDetails'
    });

    const hasContextProvider = useCheckExpandableAreaProvider();
    const { showDetailedView } = useDealSummary();

    const dealSummaryItemPrefix = skewWords(['deal', 'summary', 'item']);
    const kebabLabel = kebabCase(label);
    const labelId = skewWords([dealSummaryItemPrefix, 'label', kebabLabel]);
    const valueId = skewWords([dealSummaryItemPrefix, 'value', kebabLabel]);
    const containerTestId = skewWords([dealSummaryItemPrefix, kebabLabel]);
    const expandableOfferId = skewWords([offerType, containerTestId]);
    const className = isEmpty ? skewWords(['empty', 'item']) : '';
    const expandableId = SUMMARY_ACCORDION_ID;
    const nonZeroValue = isEnhancedDealDetailsEnabled || value !== ZERO_DOLLARS;
    const redrawDeepMenu = nonZeroValue && children;

    const createDealSummarySection = useCallback(
        (children: ReactNode): ReactNode => {
            return (
                <>
                    {!showDetailedView ? (
                        <DealSummaryExpandableView
                            containerTestId={containerTestId}
                            expandableId={expandableId}
                            expandableOfferId={expandableOfferId}
                            label={label}
                            value={value}
                            valueId={valueId}
                            labelStyles={labelStyles}
                            valueStyles={valueStyles}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            scopeId={scopeId}
                            defaultExpanded={defaultExpanded}
                            headerAction={headerAction}
                        >
                            {children}
                        </DealSummaryExpandableView>
                    ) : (
                        <DealSummaryDetailedView
                            label={label}
                            value={value}
                            labelStyles={labelStyles}
                            valueStyles={valueStyles}
                            containerTestId={containerTestId}
                            valueId={valueId}
                            expandibleButtonStyles={expandibleButtonStyles}
                            expandibleAreaStyles={expandibleAreaStyles}
                            headerAction={headerAction}
                        >
                            {children}
                        </DealSummaryDetailedView>
                    )}
                </>
            );
        },
        [
            containerTestId,
            expandableId,
            expandableOfferId,
            expandibleAreaStyles,
            expandibleButtonStyles,
            label,
            labelStyles,
            showDetailedView,
            value,
            valueId,
            valueStyles,
            scopeId,
            defaultExpanded
        ]
    );

    const createExpandableAreaProvider = useCallback(
        (children: ReactNode): ReactNode => {
            return !showDetailedView ? (
                <ExpandableAreaProvider>{createDealSummarySection(children)}</ExpandableAreaProvider>
            ) : (
                <DealSummaryProvider showDetailedView={showDetailedView}>{createDealSummarySection(children)}</DealSummaryProvider>
            );
        },
        [createDealSummarySection, showDetailedView]
    );

    const deepMenu = useCallback(
        (children: ReactNode, hasContextProvider: boolean) => {
            return (
                <StyledListItem className={`${className} ${hasDelta ? 'has-delta' : ''}`} hasDelta={hasDelta}>
                    {hasContextProvider ? createDealSummarySection(children) : createExpandableAreaProvider(children)}
                </StyledListItem>
            );
        },
        [hasContextProvider, className, hasDelta, createDealSummarySection, createExpandableAreaProvider]
    );

    if (redrawDeepMenu) {
        return deepMenu(children, hasContextProvider);
    }

    return (
        <StyledListItem data-testid={containerTestId} hasDelta={hasDelta} className={`${className} ${hasDelta ? 'has-delta' : ''}`}>
            <LineItemRow
                styles={{
                    pl: 2,
                    ...(!showDetailedView && { pt: '0.714rem', pb: '0.714rem' }),
                    ...expandibleButtonStyles
                }}
            >
                <Typography
                    tag="span"
                    variant="body-xs"
                    color={!showDetailedView ? 'sem.color.on-surface.default' : 'sem.color.on-surface.muted'}
                    data-testid={labelId}
                    className="summary-item-label"
                    sx={{
                        flexGrow: 1,
                        ...(labelAction && { display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }),
                        ...labelStyles
                    }}
                >
                    {label}
                    {labelAction}
                </Typography>
                <Typography
                    tag="span"
                    variant="strong-sm"
                    color="sem.color.on-surface.default"
                    data-testid={valueId}
                    className="summary-item-value"
                    sx={valueStyles}
                >
                    {value}
                </Typography>
                {headerAction}
            </LineItemRow>
        </StyledListItem>
    );
};

export default DealSummaryItem;
